import React, { useEffect, useState, useCallback } from 'react';
import { Plus, DownloadSimple, X } from '@phosphor-icons/react';
import {
  listProductClients, createProductClient, updateProductClient, deleteProductClient,
  listImportableClientSources, importClientsFromDataSource, getClientDataSourceLink, unlinkClientDataSource,
} from '../assets/api';
import { notifySuccess, notifyError } from '../../lib/notify';
import RowActionsMenu from '../../components/RowActionsMenu';

const EMPTY_FORM = { name: '' };

function Overlay({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '10px',
    }}>
      <div style={{ background: 'white', padding: '24px', width: '100%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>{title}</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Client accounts for a product — list/create/edit/delete, plus linking a
 * ticket-form "Clients" data source to auto-sync names in. Same client
 * entity/endpoints the Assets module's Clients tab uses; this is the
 * Product Management-side home for it, since client writes require the
 * product_management module.
 */
function ClientsPanel({ productId }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'create' | client object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [linkedSource, setLinkedSource] = useState(null);
  const [unlinking, setUnlinking] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importSources, setImportSources] = useState([]);
  const [importSourcesLoading, setImportSourcesLoading] = useState(false);
  const [selectedImportSourceId, setSelectedImportSourceId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  const refresh = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    listProductClients(productId)
      .then(setClients)
      .catch((e) => console.error('Failed to fetch clients:', e))
      .finally(() => setLoading(false));
  }, [productId]);

  const refreshLinkedSource = useCallback(() => {
    if (!productId) return;
    getClientDataSourceLink(productId)
      .then((r) => setLinkedSource(r.data_source_id ? r : null))
      .catch((e) => console.error('Failed to fetch linked data source:', e));
  }, [productId]);

  useEffect(() => { refresh(); refreshLinkedSource(); }, [refresh, refreshLinkedSource]);

  const openEdit = (client) => {
    setError(null);
    setEditing(client || 'create');
    setForm(client ? { name: client.name } : EMPTY_FORM);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editing === 'create') {
        await createProductClient(productId, form);
        notifySuccess('Client added');
      } else {
        await updateProductClient(editing.client_id, form);
        notifySuccess('Client updated');
      }
      setEditing(null);
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save client';
      setError(detail);
      notifyError(detail);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"?`)) return;
    try {
      await deleteProductClient(client.client_id);
      notifySuccess('Client deleted');
      refresh();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleUnlinkSource = async () => {
    if (!window.confirm('Stop auto-syncing clients from this data source? Existing clients stay as-is.')) return;
    setUnlinking(true);
    try {
      await unlinkClientDataSource(productId);
      setLinkedSource(null);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to unlink data source');
    } finally {
      setUnlinking(false);
    }
  };

  const openImport = () => {
    setImportError(null);
    setImportResult(null);
    setSelectedImportSourceId(linkedSource?.data_source_id || '');
    setImportOpen(true);
    setImportSourcesLoading(true);
    listImportableClientSources(productId)
      .then(setImportSources)
      .catch((e) => setImportError(e.response?.data?.detail || 'Failed to load data sources'))
      .finally(() => setImportSourcesLoading(false));
  };

  const runImport = async () => {
    if (!selectedImportSourceId) return;
    setImporting(true);
    setImportError(null);
    try {
      const result = await importClientsFromDataSource(productId, selectedImportSourceId);
      setImportResult(result);
      refresh();
      refreshLinkedSource();
    } catch (err) {
      setImportError(err.response?.data?.detail || 'Failed to link/sync clients');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {linkedSource ? (
          <div style={{ fontSize: '12px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="badge" style={{ background: '#DBEAFE' }}>Auto-syncing</span>
            from <strong>{linkedSource.name}</strong>
            <button type="button" onClick={handleUnlinkSource} disabled={unlinking}
              style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: '#B91C1C', padding: 0 }}>
              {unlinking ? 'Unlinking…' : 'Unlink'}
            </button>
          </div>
        ) : <span />}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={openImport} className="tk-btn tk-btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <DownloadSimple size={16} weight="bold" /> {linkedSource ? 'Change Data Source' : 'Link Data Source'}
          </button>
          <button type="button" onClick={() => openEdit(null)} className="tk-btn tk-btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} weight="bold" /> Add Client
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ fontSize: '12px', color: '#6B7280' }}>Loading…</p>
      ) : clients.length === 0 ? (
        <p style={{ fontSize: '12px', color: '#6B7280' }}>No clients yet. Add the client accounts this product serves.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {clients.map((c) => (
            <div key={c.client_id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white',
            }}>
              <p style={{ fontSize: '13px', fontWeight: '600', margin: 0 }}>
                {c.name} {!c.active && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>}
              </p>
              <RowActionsMenu actions={[
                { label: 'Edit', onClick: () => openEdit(c) },
                { label: 'Delete', onClick: () => remove(c), danger: true },
              ]} />
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Overlay title={editing === 'create' ? 'Add Client' : 'Edit Client'} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Name *</label>
              <input type="text" value={form.name} required className="input-field"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            {error && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="tk-btn tk-btn-primary" style={{ flex: 1 }} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="tk-btn tk-btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </form>
        </Overlay>
      )}

      {importOpen && (
        <Overlay title="Link Clients Data Source" onClose={() => setImportOpen(false)}>
          <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '10px' }}>
            Link this product to one of its ticket-form data sources (e.g. a "Clients" dropdown that already
            lists real client names). Every time this tab is opened afterward, any new names found there are
            added here automatically — existing clients are never renamed or removed.
          </p>

          {importSourcesLoading ? (
            <p style={{ fontSize: '12px', color: '#6B7280' }}>Loading data sources…</p>
          ) : importSources.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#6B7280' }}>No static-list data sources found on this product.</p>
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Data source</label>
              <select value={selectedImportSourceId} className="input-field"
                onChange={(e) => { setSelectedImportSourceId(e.target.value); setImportResult(null); }}>
                <option value="">Select a data source…</option>
                {importSources.map((s) => (
                  <option key={s.data_source_id} value={s.data_source_id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {importError && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '8px' }}>{importError}</p>}

          {importResult && (
            <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: '#ECFDF5', fontSize: '12px' }}>
              <p style={{ fontWeight: '600', color: '#065F46', margin: 0 }}>
                Linked — {importResult.created} client{importResult.created === 1 ? '' : 's'} added
                {importResult.skipped > 0 && ` · ${importResult.skipped} already existed`}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button type="button" onClick={runImport} className="tk-btn tk-btn-primary" style={{ flex: 1 }}
              disabled={!selectedImportSourceId || importing}>
              {importing ? 'Linking…' : 'Link & Sync Now'}
            </button>
            <button type="button" onClick={() => setImportOpen(false)} className="tk-btn tk-btn-secondary" style={{ flex: 1 }}>
              Close
            </button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

export default ClientsPanel;
